import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useCallback, useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { CheckboxField, Field } from './Master';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface ICompany1099 {
    id: number;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    zip4?: string;
    phone?: string;
    extension?: string;
    taxId?: string;
    stateId?: string;
    glAccountNumber?: string;
    parent?: string;
    isActive?: boolean;
    nameOnLetter?: string;
    bankAccountNumber?: string;
    beginningCheckNumber?: number;
    endCheckNumber?: number;
}

interface ICompanyRow extends ICompany1099 {
    rowId: string;
}

interface ICompanyProspect {
    compId?: number;
    clNo?: number;
    prNo?: number;
}

interface ICompanyProspectRow {
    rowId: string;
    clNo: string;
    prNo: string;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

type ViewMode = 'list' | 'form';

const PAGE_SIZE = 25;
const MIN_PROSPECT_ROWS = 14;

const createProspectRows = (items?: ICompanyProspect[]): ICompanyProspectRow[] => {
    const mapped = (items || []).map((item, index) => ({
        rowId: String(index),
        clNo: item?.clNo !== null && item?.clNo !== undefined ? String(item.clNo) : '',
        prNo: item?.prNo !== null && item?.prNo !== undefined ? String(item.prNo) : '',
    }));

    const minRows = Math.max(MIN_PROSPECT_ROWS, mapped.length + 2);
    const rows = [...mapped];
    while (rows.length < minRows) {
        rows.push({
            rowId: `blank-${rows.length}`,
            clNo: '',
            prNo: '',
        });
    }

    return rows;
};

const normalizeCompany = (row: Partial<ICompany1099>): ICompanyRow => {
    const id = Number(row.id || 0);
    return {
        rowId: String(id),
        id,
        name: row.name || '',
        address: row.address || '',
        city: row.city || '',
        state: row.state || '',
        zip: row.zip || '',
        zip4: row.zip4 || '',
        phone: row.phone || '',
        extension: row.extension || '',
        taxId: row.taxId || '',
        stateId: row.stateId || '',
        glAccountNumber: row.glAccountNumber || '',
        parent: row.parent || '',
        isActive: row.isActive !== false,
        nameOnLetter: row.nameOnLetter || '',
        bankAccountNumber: row.bankAccountNumber || '',
        beginningCheckNumber: Number(row.beginningCheckNumber || 0) || 0,
        endCheckNumber: Number(row.endCheckNumber || 0) || 0,
    };
};

const toPayload = (row: ICompanyRow): object => {
    return {
        id: row.id,
        name: row.name || '',
        address: row.address || '',
        city: row.city || '',
        state: row.state || '',
        zip: row.zip || '',
        zip4: row.zip4 || '',
        phone: row.phone || '',
        extension: row.extension || '',
        taxId: row.taxId || '',
        stateId: row.stateId || '',
        glAccountNumber: row.glAccountNumber || '',
        parent: row.parent || '',
        isActive: row.isActive === true,
        nameOnLetter: row.nameOnLetter || '',
        bankAccountNumber: row.bankAccountNumber || '',
        beginningCheckNumber: row.beginningCheckNumber || 0,
        endCheckNumber: row.endCheckNumber || 0,
    };
};

const Company1099MaintenanceDialog: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, post, put, response } = useFetch();

    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(true);
    const [rows, setRows] = useState<PagedResultSet<ICompanyRow> | undefined>();
    const [editing, setEditing] = useState<ICompanyRow | null>(null);
    const [prospects, setProspects] = useState<ICompanyProspectRow[]>(createProspectRows());
    const [isProspectsLoading, setIsProspectsLoading] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const columns: ITableColumn[] = [
        { name: 'Company ID', mapping: 'id' },
        { name: 'Name', mapping: 'name' },
        { name: 'City', mapping: 'city' },
        { name: 'State', mapping: 'state' },
        { name: 'Phone', mapping: 'phone' },
    ];

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'rowId',
        columns,
    };

    const loadRows = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        setRequestError(null);
        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());
            if (search.trim()) {
                params.append('search', search.trim());
            }

            const data = await get(`/v1.0/leases/admin/1099/companies?${params.toString()}`);
            if (!response.ok || !data) {
                setRows(undefined);
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to load 1099 company records.' }, response.status));
                return;
            }

            const mapped: ICompanyRow[] = (data.items || []).map((row: Partial<ICompany1099>) => normalizeCompany(row));
            const paged = new PagedResultSet<ICompanyRow>();
            paged.items = mapped;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setRows(paged);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error loading 1099 company records.' }, 500));
            setRows(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [get, pageNumber, response, search]);

    const loadProspects = useCallback(
        async (compId: number): Promise<void> => {
            if (!compId) {
                setProspects(createProspectRows());
                return;
            }

            setIsProspectsLoading(true);
            try {
                const data = await get(`/v1.0/leases/admin/1099/companies/${compId}/prospects`);
                if (!response.ok || !Array.isArray(data)) {
                    setProspects(createProspectRows());
                    return;
                }

                setProspects(createProspectRows(data as ICompanyProspect[]));
            } catch (error) {
                setProspects(createProspectRows());
            } finally {
                setIsProspectsLoading(false);
            }
        },
        [get, response]
    );

    useEffect(() => {
        if (isOpen) {
            setViewMode('list');
            setEditing(null);
            setIsCreating(false);
            setPageNumber(1);
            setShouldReload(true);
            setProspects(createProspectRows());
            setRequestError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || viewMode !== 'list' || !shouldReload) {
            return;
        }

        void loadRows();
        setShouldReload(false);
    }, [isOpen, loadRows, shouldReload, viewMode]);

    useEffect(() => {
        if (!isOpen || viewMode !== 'form') {
            return;
        }

        if (isCreating || !editing?.id) {
            setProspects(createProspectRows());
            return;
        }

        void loadProspects(editing.id);
    }, [editing?.id, isCreating, isOpen, loadProspects, viewMode]);

    const openCreate = async (): Promise<void> => {
        let nextId = Math.max(0, ...(rows?.items || []).map((item) => item.id || 0)) + 1;
        try {
            const data = await get('/v1.0/leases/admin/1099/companies/next-id');
            if (response.ok && data && typeof data.nextId === 'number') {
                nextId = data.nextId;
            } else {
                alert('Unable to auto-assign from server. Using local next Company ID.');
            }
        } catch (error) {
            alert('Unable to auto-assign from server. Using local next Company ID.');
        }

        setEditing(
            normalizeCompany({
                id: nextId,
                isActive: true,
            })
        );
        setProspects(createProspectRows());
        setIsCreating(true);
        setViewMode('form');
    };

    const handleRowClick = (id: string | number): void => {
        const selected = rows?.items.find((item) => item.rowId === id.toString());
        if (!selected) {
            return;
        }

        setEditing(normalizeCompany(selected));
        setIsCreating(false);
        setViewMode('form');
    };

    const handleProspectChange = (index: number, field: 'clNo' | 'prNo', value: string): void => {
        const digitsOnly = (value || '').replace(/[^\d]/g, '');
        setProspects((prev) => {
            const next = [...prev];
            if (!next[index]) {
                return prev;
            }

            next[index] = {
                ...next[index],
                [field]: digitsOnly,
            };

            return next;
        });
    };

    const handleSave = async (): Promise<void> => {
        if (!editing) {
            return;
        }

        if (!editing.id || !editing.name.trim()) {
            alert('Company ID and Name are required.');
            return;
        }

        setIsSaving(true);
        setRequestError(null);
        try {
            const payload = toPayload(editing);
            if (isCreating) {
                await post('/v1.0/leases/admin/1099/companies', payload);
            } else {
                await put('/v1.0/leases/admin/1099/companies', payload);
            }

            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save 1099 company record.' }, response.status));
                return;
            }

            const prospectPayload = prospects
                .map((item) => {
                    const clNo = parseInt(item.clNo, 10);
                    const prNo = parseInt(item.prNo, 10);
                    if (Number.isNaN(clNo) || Number.isNaN(prNo)) {
                        return null;
                    }

                    return {
                        compId: editing.id,
                        clNo,
                        prNo,
                    };
                })
                .filter((item) => item !== null);

            await put(`/v1.0/leases/admin/1099/companies/${editing.id}/prospects`, prospectPayload);
            if (!response.ok) {
                setRequestError(
                    parseLeaseRecordsApiError(
                        response.data || { title: 'Company saved, but failed to save Check Default Prospects.' },
                        response.status
                    )
                );
                return;
            }

            setViewMode('list');
            setEditing(null);
            setIsCreating(false);
            setPageNumber(1);
            setShouldReload(true);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error saving 1099 company record.' }, 500));
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.content} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h3 style={styles.title}>1099 Company Maintenance</h3>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close 1099 Company dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={styles.body}>
                    {viewMode === 'list' ? (
                        <>
                            <div style={styles.filterRow}>
                                <Field label="Search" value={search} editable={true} onChange={(value) => setSearch(value || '')} />
                            </div>

                            <div style={styles.actionRow}>
                                <button
                                    onClick={() => {
                                        setPageNumber(1);
                                        setShouldReload(true);
                                    }}
                                    style={styles.secondaryButton}
                                >
                                    Query
                                </button>
                                <button onClick={() => void openCreate()} style={styles.primaryButton}>
                                    Add Company
                                </button>
                            </div>

                            <div className="search-leases__table-wrapper" style={styles.tableSection}>
                                <Table
                                    isLoading={isLoading}
                                    tableConfig={tableConfig}
                                    data={rows}
                                    enablePaging={true}
                                    onPageChange={(newPage) => {
                                        setPageNumber(newPage);
                                        setShouldReload(true);
                                    }}
                                    onRowClick={handleRowClick}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={styles.formLayout}>
                                <div style={styles.formLeft}>
                                    <div style={styles.formGrid}>
                                        <Field
                                            label="Company ID"
                                            value={String(editing?.id || '')}
                                            editable={false}
                                            onChange={(value) =>
                                                setEditing((prev) => (prev ? { ...prev, id: parseInt(value || '0', 10) || 0 } : prev))
                                            }
                                        />
                                        <Field
                                            label="Name"
                                            value={editing?.name || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, name: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Address"
                                            value={editing?.address || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, address: value || '' } : prev))}
                                        />
                                        <Field
                                            label="City"
                                            value={editing?.city || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, city: value || '' } : prev))}
                                        />
                                        <Field
                                            label="State"
                                            value={editing?.state || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, state: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Zip"
                                            value={editing?.zip || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, zip: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Zip4"
                                            value={editing?.zip4 || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, zip4: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Phone"
                                            value={editing?.phone || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, phone: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Extension"
                                            value={editing?.extension || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, extension: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Tax ID"
                                            value={editing?.taxId || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, taxId: value || '' } : prev))}
                                        />
                                        <Field
                                            label="State ID"
                                            value={editing?.stateId || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, stateId: value || '' } : prev))}
                                        />
                                        <Field
                                            label="GL Account No"
                                            value={editing?.glAccountNumber || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, glAccountNumber: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Parent"
                                            value={editing?.parent || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, parent: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Name on Letter"
                                            value={editing?.nameOnLetter || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, nameOnLetter: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Bank Account No"
                                            value={editing?.bankAccountNumber || ''}
                                            editable={true}
                                            onChange={(value) => setEditing((prev) => (prev ? { ...prev, bankAccountNumber: value || '' } : prev))}
                                        />
                                        <Field
                                            label="Beginning Check No"
                                            value={String(editing?.beginningCheckNumber || '')}
                                            editable={true}
                                            onChange={(value) =>
                                                setEditing((prev) =>
                                                    prev ? { ...prev, beginningCheckNumber: parseInt(value || '0', 10) || 0 } : prev
                                                )
                                            }
                                        />
                                        <Field
                                            label="Ending Check No"
                                            value={String(editing?.endCheckNumber || '')}
                                            editable={true}
                                            onChange={(value) =>
                                                setEditing((prev) => (prev ? { ...prev, endCheckNumber: parseInt(value || '0', 10) || 0 } : prev))
                                            }
                                        />
                                        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                            <CheckboxField
                                                label="Active"
                                                checked={editing?.isActive !== false}
                                                editable={true}
                                                onChange={(checked) => setEditing((prev) => (prev ? { ...prev, isActive: checked } : prev))}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.prospectSection}>
                                    <h4 style={styles.prospectTitle}>Check Default Prospects</h4>
                                    <div style={styles.prospectHeaderRow}>
                                        <div style={styles.prospectHeaderCell}>Client No.</div>
                                        <div style={styles.prospectHeaderCell}>Pr. No.</div>
                                    </div>
                                    <div style={styles.prospectRows}>
                                        {isProspectsLoading ? (
                                            <div style={styles.prospectLoading}>Loading...</div>
                                        ) : (
                                            prospects.map((row, index) => (
                                                <div key={row.rowId} style={styles.prospectRow}>
                                                    <LeaseRecordInput
                                                        style={styles.prospectInput}
                                                        value={row.clNo}
                                                        onChange={(event) => handleProspectChange(index, 'clNo', event.target.value)}
                                                        inputMode="numeric"
                                                    />
                                                    <LeaseRecordInput
                                                        style={styles.prospectInput}
                                                        value={row.prNo}
                                                        onChange={(event) => handleProspectChange(index, 'prNo', event.target.value)}
                                                        inputMode="numeric"
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={styles.actionRow}>
                                <button onClick={handleSave} style={styles.primaryButton} disabled={isSaving}>
                                    <div style={styles.buttonInner}>
                                        {isSaving && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                        <span>{isSaving ? 'Saving...' : 'Save Record'}</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => {
                                        setViewMode('list');
                                        setEditing(null);
                                        setIsCreating(false);
                                        setProspects(createProspectRows());
                                    }}
                                    style={styles.secondaryButton}
                                    disabled={isSaving}
                                >
                                    Close Screen
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 95,
        padding: '1rem',
    },
    content: {
        width: 'min(1280px, 96vw)',
        maxHeight: '92vh',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: '0 24px 48px rgba(17, 24, 39, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1rem',
        borderBottom: '1px solid #E5E7EB',
    },
    title: {
        margin: 0,
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#1F2937',
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        color: '#6B7280',
        cursor: 'pointer',
        padding: '0.2rem',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.9rem',
        padding: '0.9rem',
        overflow: 'hidden',
        minHeight: 0,
        flex: 1,
    },
    filterRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: '0.7rem',
    },
    tableSection: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.6rem',
    },
    buttonInner: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    primaryButton: {
        border: 'none',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#1D4ED8',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        border: '1px solid #CBD5E1',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
        cursor: 'pointer',
        fontWeight: 600,
    },
    formLayout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 280px',
        gap: '0.9rem',
        minHeight: 0,
        flex: 1,
    },
    formLeft: {
        minWidth: 0,
        overflow: 'auto',
        paddingRight: '0.25rem',
    },
    formGrid: {
        display: 'grid',
        gap: '0.7rem',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
    prospectSection: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #CBD5E1',
        borderRadius: '0.4rem',
        padding: '0.5rem',
        backgroundColor: '#F8FAFC',
    },
    prospectTitle: {
        margin: '0 0 0.5rem',
        fontSize: '0.95rem',
        fontWeight: 700,
        color: '#111827',
        textAlign: 'center',
    },
    prospectHeaderRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.35rem',
        marginBottom: '0.35rem',
    },
    prospectHeaderCell: {
        fontSize: '0.85rem',
        fontWeight: 700,
        color: '#1F2937',
        textAlign: 'center',
    },
    prospectRows: {
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflowY: 'auto',
        paddingRight: '0.2rem',
    },
    prospectRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.35rem',
        marginBottom: '0.25rem',
    },
    prospectInput: {
        width: '100%',
        height: '1.85rem',
        border: '1px solid #94A3B8',
        borderRadius: '0.2rem',
        padding: '0 0.45rem',
        fontSize: '0.9rem',
        color: '#0F172A',
        backgroundColor: '#FFFFFF',
        outline: 'none',
    },
    prospectLoading: {
        fontSize: '0.85rem',
        color: '#64748B',
        textAlign: 'center',
        padding: '0.6rem 0',
    },
};

export default Company1099MaintenanceDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Company1099MaintenanceDialog.tsx